import React, { useEffect, useRef } from 'react'
import { Grid, H1, Separator, Skeleton, useContainerWidth } from '@fs/zion-ui'
import { Redirect } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { exhaustiveCheck } from '@fs/zion-tree-types'
import { useTreeGroupDetailsContext } from '../providers/TreeGroupDetailsProvider'
import SideNav, { SideNavSkeleton, TopNav, TopNavSkeleton } from './SideNav'
import { getTabUrl, type TabName, useTreeSettingsParams, useTreeSettingsRouteMatch } from '../util'
import DetailsContent, { DetailsContentSkeleton } from './DetailsContent'
import ContributorsContent, { ContributorsContentSkeleton } from './ContributorsContent'
import PrivacyContent, { PrivacyContentSkeleton } from './PrivacyContent'
import { useMembersContext } from '../../providers/MembersProvider'
import LatestChangesContent from './LatestChangesContent'
import { useStartingPerson } from '../../providers/StartingPersonProvider'
import { useUserIsDataAdmin } from '../../providers/DataAdminProvider'
import SkeletonAnimationSyncDecorator from '../../components/SkeletonAnimationSyncDecorator'
import { TreeChangesContentSkeleton } from '../../components/details/researcher-trees/tabs/TreeChangesCard'

const subPageContentSkeletonMap = {
  'latest-changes': TreeChangesContentSkeleton,
  contributors: ContributorsContentSkeleton,
  details: DetailsContentSkeleton,
  privacy: PrivacyContentSkeleton,
} as const satisfies Record<TabName, unknown>

function TabContent(): JSX.Element {
  const { tab } = useTreeSettingsParams()
  const { userIsAdmin } = useMembersContext()
  const match = useTreeSettingsRouteMatch()

  // Parent route already handles tab parameter - render based on current tab
  switch (tab) {
    case 'details':
      return <DetailsContent />
    case 'privacy':
      return userIsAdmin ? <PrivacyContent /> : <Redirect to={getTabUrl('details', match)} />
    case 'latest-changes':
      return <LatestChangesContent />
    case 'contributors':
      return <ContributorsContent />
    default:
      return exhaustiveCheck(tab)
  }
}

export default function SettingsContent(): JSX.Element | null {
  const [t] = useTranslation()
  const { group } = useTreeGroupDetailsContext()
  const { tab } = useTreeSettingsParams()
  const { loading, startingPersonId, startingPersonOverlay } = useStartingPerson()
  const { userIsDataAdmin } = useUserIsDataAdmin()
  const { userIsAdmin } = useMembersContext()
  const hasOpenedStartingPerson = useRef(false)
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })

  useEffect(() => {
    if (!loading && !startingPersonId && !hasOpenedStartingPerson.current && userIsAdmin && !userIsDataAdmin) {
      hasOpenedStartingPerson.current = true
      startingPersonOverlay.open()
    }
  }, [loading, startingPersonId, startingPersonOverlay, userIsAdmin, userIsDataAdmin])

  return (
    <div>
      <H1 size="H4">{t('group-management.tree-settings.heading', { name: group.name })}</H1>
      <Separator />

      {isMobile ? (
        <Grid gutters="nano">
          <TopNav tab={tab} />
          <TabContent />
        </Grid>
      ) : (
        <>
          <Grid>
            <SideNav columns={3} tab={tab} />
            <TabContent columns={9} />
          </Grid>
          {/* Spacing for Help Assistant to be out of the way */}
          <Separator size="lg" />
        </>
      )}
    </div>
  )
}

type SettingsContentSkeletonProps = {
  ContentSkeleton: (() => JSX.Element) | 'div'
}

export function SettingsContentSkeleton({ ContentSkeleton }: SettingsContentSkeletonProps): JSX.Element {
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })

  return (
    <SkeletonAnimationSyncDecorator>
      <Skeleton.H4 />
      <Separator />
      {isMobile ? (
        <Grid gutters="nano">
          <TopNavSkeleton />
          <ContentSkeleton />
        </Grid>
      ) : (
        <Grid>
          <SideNavSkeleton columns={3} />
          <ContentSkeleton columns={9} />
        </Grid>
      )}
    </SkeletonAnimationSyncDecorator>
  )
}

export function TabSettingsContentSkeleton(): JSX.Element {
  const { tab } = useTreeSettingsParams()
  const ContentSkeleton = subPageContentSkeletonMap[tab]
  return (
    <SkeletonAnimationSyncDecorator>
      <SettingsContentSkeleton ContentSkeleton={ContentSkeleton} />
    </SkeletonAnimationSyncDecorator>
  )
}
